// Per-hub-location matrix editor for `Model.hubPct`.
//
// Replaces the old "Offshore % / Nearshore %" two-tab editor. Each tab now
// represents a real hub location from `model.ogsHubs` (e.g. "Mexico",
// "Lebanon", "India (OGS)") so the names stay synced with the intake's OGS
// card and the staff plan's hub picker.
//
// Engine semantics: these per-hub %s are now DEFAULTS only. The shore split is
// scenario-driven — each scenario configures OGS per discipline (enable, hub,
// a single %, and which levels) in the intake's OGS card. When a user enables a
// discipline / picks a hub, these hubPct rows pre-fill a suggested % and the
// affected levels (via `deriveDefaultOgsForDiscipline`); the scenario values
// then drive the calculation. Editing here changes the suggested starting
// point, not the final split for existing scenarios.

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { MapPin } from 'lucide-react';
import { isOgsEligible } from '@rc/engine';
import type { HubPctRow, LevelId, Model, OgsHub } from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function OffshorePctMatrix({ model, onChange }: Props) {
  const hubs = useMemo(() => model.ogsHubs ?? [], [model.ogsHubs]);

  const [activeHubId, setActiveHubId] = useState<string>(
    () => hubs[0]?.id ?? '',
  );

  // If hubs are added/removed in the Buckets & Hubs tab while we're mounted,
  // keep the active tab pointing at a real hub.
  useEffect(() => {
    if (hubs.length === 0) return;
    if (!hubs.some((h) => h.id === activeHubId)) {
      setActiveHubId(hubs[0].id);
    }
  }, [hubs, activeHubId]);

  if (hubs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-6 text-center">
        <MapPin
          size={20}
          className="mx-auto text-slate-400"
          aria-hidden="true"
        />
        <p className="mt-2 text-sm text-slate-700 font-medium">
          No hub locations defined yet.
        </p>
        <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto">
          Add hub locations on the Buckets &amp; Hubs tab. Each hub becomes a
          tab here, and the same hubs flow through to the intake's OGS card and
          the staff plan's Hub-location picker.
        </p>
      </div>
    );
  }

  const activeHub = hubs.find((h) => h.id === activeHubId) ?? hubs[0];

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 max-w-2xl">
        Recommended share of each (discipline × channel × level) cell to land
        at this hub. Values are 0..1. Each scenario picks one hub per shore
        in the intake's OGS card; that hub's table is what the engine uses.
        Add or rename hubs on the <span className="font-medium text-slate-600">Buckets &amp; hubs</span> tab.
      </p>
      <p className="text-xs text-slate-500 max-w-2xl">
        The shaded <span className="font-medium text-slate-600">Expose</span> column is a single
        setting per discipline/channel that is <span className="font-medium text-slate-600">shared
        across every hub tab</span> — it controls whether that row is offered as an OGS slider in
        the scenario intake. Untick it once (on any tab) to remove it everywhere. The per-hub
        <span className="font-medium text-slate-600"> % </span> values stay independent per tab.
      </p>

      {/* Hub tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 -mb-px">
        {hubs.map((h) => (
          <HubTabButton
            key={h.id}
            hub={h}
            active={h.id === activeHub.id}
            onClick={() => setActiveHubId(h.id)}
          />
        ))}
      </div>

      <HubMatrix model={model} onChange={onChange} hub={activeHub} />
    </div>
  );
}

function HubTabButton({
  hub,
  active,
  onClick,
}: {
  hub: OgsHub;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors flex items-center gap-1.5',
        active
          ? 'border-brand-purple-500 text-brand-purple-700 bg-brand-purple-50/40'
          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50',
      )}
    >
      <MapPin size={12} className={active ? 'text-brand-purple-500' : 'text-slate-400'} />
      <span>{hub.label}</span>
      <span
        className={clsx(
          'pill text-[10px] ml-0.5',
          hub.shore === 'nearshore'
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-amber-50 text-amber-800',
        )}
      >
        {hub.shore}
      </span>
    </button>
  );
}

function HubMatrix({
  model,
  onChange,
  hub,
}: {
  model: Model;
  onChange: (m: Model) => void;
  hub: OgsHub;
}) {
  const pairs = useMemo(() => {
    const seen = new Set<string>();
    const out: { disciplineId: string; channelId: string }[] = [];
    for (const sp of model.spendPerFte || []) {
      const k = `${sp.disciplineId}::${sp.channelId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ disciplineId: sp.disciplineId, channelId: sp.channelId });
    }
    return out;
  }, [model.spendPerFte]);

  const levels = model.levels || [];

  const findRow = (
    disciplineId: string,
    channelId: string,
    levelId: LevelId,
  ): HubPctRow | undefined =>
    (model.hubPct || []).find(
      (r) =>
        r.hubId === hub.id &&
        r.disciplineId === disciplineId &&
        r.channelId === channelId &&
        r.levelId === levelId,
    );

  const setPct = (
    disciplineId: string,
    channelId: string,
    levelId: LevelId,
    pct: number,
  ) => {
    const clamped = Math.max(0, Math.min(1, pct));
    const next = [...(model.hubPct || [])];
    const idx = next.findIndex(
      (r) =>
        r.hubId === hub.id &&
        r.disciplineId === disciplineId &&
        r.channelId === channelId &&
        r.levelId === levelId,
    );
    if (idx >= 0) {
      next[idx] = { ...next[idx], pct: clamped };
    } else {
      next.push({ hubId: hub.id, disciplineId, channelId, levelId, pct: clamped });
    }
    onChange({ ...model, hubPct: next });
  };

  const discLabel = (id: string) =>
    (model.disciplines || []).find((d) => d.id === id)?.label || id;
  const chanLabel = (id: string) =>
    id === 'all' ? 'All channels' : (model.channels || []).find((c) => c.id === id)?.label || id;

  // OGS eligibility is a per-(discipline × channel) gate shared across hubs:
  // toggling it here controls whether the row's OGS slider appears in the
  // scenario intake. Missing entry = eligible.
  const setEligible = (disciplineId: string, channelId: string, enabled: boolean) => {
    const list = [...(model.ogsEligibility || [])];
    const idx = list.findIndex(
      (e) => e.disciplineId === disciplineId && e.channelId === channelId,
    );
    if (idx >= 0) list[idx] = { ...list[idx], enabled };
    else list.push({ disciplineId, channelId, enabled });
    onChange({ ...model, ogsEligibility: list });
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Discipline</th>
            <th className="text-left px-3 py-2">Channel</th>
            <th
              className="text-center px-3 py-2 bg-slate-100 border-l-2 border-r-2 border-slate-300 text-slate-600"
              title="Shared across all hub tabs — offer this row's OGS slider in the scenario intake"
            >
              <div className="leading-tight">
                Expose
                <div className="text-[9px] font-normal normal-case tracking-normal text-slate-400">
                  all hubs
                </div>
              </div>
            </th>
            {levels.map((l) => (
              <th key={l.id} className="text-right px-3 py-2 tabular-nums">
                {l.id}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pairs.map(({ disciplineId, channelId }) => {
            const eligible = isOgsEligible(model, disciplineId, channelId);
            return (
              <tr
                key={`${disciplineId}::${channelId}`}
                className={clsx('border-t border-slate-100', !eligible && 'bg-slate-50/60')}
              >
                <td
                  className={clsx(
                    'px-3 py-1.5 sticky left-0 font-medium',
                    eligible ? 'bg-white text-slate-700' : 'bg-slate-50/60 text-slate-400',
                  )}
                >
                  {discLabel(disciplineId)}
                </td>
                <td className={clsx('px-3 py-1.5 text-xs', eligible ? 'text-slate-500' : 'text-slate-400')}>
                  {chanLabel(channelId)}
                </td>
                <td className="px-3 py-1.5 text-center bg-slate-50 border-l-2 border-r-2 border-slate-300">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-brand-purple-600"
                      checked={eligible}
                      onChange={(e) => setEligible(disciplineId, channelId, e.target.checked)}
                      title={
                        eligible
                          ? 'Shown in the scenario OGS input — untick to hide (applies to all hubs)'
                          : 'Hidden from the scenario OGS input — tick to show (applies to all hubs)'
                      }
                    />
                    <span
                      className={clsx(
                        'text-[10px] font-medium uppercase tracking-wide',
                        eligible ? 'text-brand-purple-700' : 'text-slate-400',
                      )}
                    >
                      {eligible ? 'On' : 'Off'}
                    </span>
                  </label>
                </td>
                {levels.map((l) => {
                  const cur = findRow(disciplineId, channelId, l.id)?.pct ?? 0;
                  return (
                    <td key={l.id} className="px-1 py-1 align-middle min-w-[78px]">
                      <input
                        type="number"
                        step={0.01}
                        min={0}
                        max={1}
                        className="input py-1 px-2 w-full text-right text-xs tabular-nums"
                        value={cur}
                        onChange={(e) =>
                          setPct(disciplineId, channelId, l.id, Number(e.target.value))
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
